import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Card,
  CardContent,
  Avatar,
} from '@mui/material';
import {
  BarChart as BarChartIcon,
  DirectionsCar as CarIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { format, subMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { EXPENSE_CATEGORIES, EXPENSE_CATEGORY_COLORS, ExpenseCategory } from '../types';
import reportService from '../services/reportService';
import { ReportData } from '../services/reportService';
import saleService from '../services/saleService';
import vehicleService from '../services/vehicleService';
import {
  buildProfitByMonthFromSales,
  buildProfitByVehicleTypeFromSales,
  sumCashRecognizedProfit,
} from '../utils/saleRealProfit';
import { 
  PieChartComponent, 
  BarChartComponent, 
  LineChartComponent, 
  DualAxisChartComponent,
  MonthlyBarChart
} from '../components/charts';

const Reports: React.FC = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<'3m' | '6m' | '1y' | 'all'>('all');
  const [reportData, setReportData] = useState<ReportData | null>(null);

  // Carregar dados do relatório
  useEffect(() => {
    const fetchReportData = async () => {
      try {
        setLoading(true);
        // Usar o serviço real
        const data = await reportService.getReportData(dateRange);
        let salesForCash = await saleService.getAllSales().catch(() => []);
        const missingVehicle = salesForCash.some((s) => s.vehicle_id && !s.vehicle);
        if (missingVehicle) {
          try {
            const vehicles = await vehicleService.getAllVehicles();
            const byId: Record<number, (typeof vehicles)[number]> = {};
            (vehicles || []).forEach((v) => {
              byId[v.id] = v;
            });
            salesForCash = salesForCash.map((s) =>
              s.vehicle ? s : { ...s, vehicle: byId[s.vehicle_id] ?? undefined }
            );
          } catch {
            // mantém vendas sem veículo enriquecido
          }
        }
        salesForCash = await saleService.enrichTermSalesWithInstallments(salesForCash);
        const cashProfitTotal = sumCashRecognizedProfit(salesForCash);
        const profitByMonth = buildProfitByMonthFromSales(salesForCash);
        const profitByVehicleType = buildProfitByVehicleTypeFromSales(salesForCash);

        setReportData({
          ...data,
          stats: {
            ...(data.stats || {}),
            totalProfit: cashProfitTotal,
          },
          sales: {
            ...(data.sales || {}),
            profit: {
              ...(data.sales?.profit || {}),
              total: cashProfitTotal,
              byMonth: profitByMonth,
              byVehicleType: profitByVehicleType,
            },
          },
        });
        setLoading(false);
      } catch (err) {
        console.error('Erro ao carregar relatórios:', err);
        setError('Não foi possível carregar os relatórios. Tente novamente mais tarde.');
        setLoading(false);
      }
    };

    fetchReportData();
  }, [dateRange]);

  const handleDateRangeChange = (event: SelectChangeEvent<string>) => {
    setDateRange(event.target.value as '3m' | '6m' | '1y' | 'all');
  };

  // Função para ordenar dados por data
  const sortDataByDate = (data: any[]) => {
    return [...data].sort((a, b) => {
      // Converte a string de data para objeto Date para comparação
      const dateA = new Date(a.month + '-01'); // Adiciona dia para criar uma data válida
      const dateB = new Date(b.month + '-01');
      return dateA.getTime() - dateB.getTime();
    });
  };

  // Formatar dados para gráficos
  const formatMonthlyData = (dateRange: '3m' | '6m' | '1y' | 'all', data: any[]) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return [];
    }

    // Primeiro ordenamos os dados cronologicamente
    const sortedData = sortDataByDate(data);
    
    const currentDate = new Date();
    let startDate: Date;
    
    if (dateRange === '3m') {
      startDate = subMonths(currentDate, 3);
    } else if (dateRange === '6m') {
      startDate = subMonths(currentDate, 6);
    } else if (dateRange === '1y') {
      startDate = subMonths(currentDate, 12);
    } else {
      // Para 'all', usamos todos os dados disponíveis
      return sortedData;
    }

    return sortedData.filter(item => {
      if (!item || !item.month) return false;
      
      const itemDate = new Date(item.month + '-01'); // Adiciona dia para criar uma data válida
      return !isNaN(itemDate.getTime()) && itemDate >= startDate;
    });
  };

  // Função segura para formatar datas
  const formatMonthDate = (dateString: string, showYear: boolean = true) => {
    try {
      if (!dateString) return '-';

      // Se for um formato numérico (2025-01), transformar em nome do mês
      if (dateString.match(/^\d{4}-\d{2}$/)) {
        const [year, month] = dateString.split('-');
        const date = new Date(parseInt(year), parseInt(month) - 1, 1); // Mês em JavaScript começa em 0
        
        // Retornar mês e ano para diferenciar dados de anos diferentes
        if (showYear) {
          return format(date, 'MMM/yy', { locale: ptBR });
        }
        return format(date, 'MMM', { locale: ptBR });
      }

      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return '-';
      }
      
      if (showYear) {
        return format(date, 'MMM/yy', { locale: ptBR });
      }
      return format(date, 'MMM', { locale: ptBR });
    } catch (error) {
      console.error('Erro ao formatar data:', dateString, error);
      return '-';
    }
  };

  // Formata valores para moeda
  const formatCurrency = (value: number = 0) => {
    return `R$ ${value.toLocaleString('pt-BR')}`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!reportData) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        Sem dados disponíveis para o período selecionado.
      </Alert>
    );
  }

  // Verificar se as estatísticas estão disponíveis, caso não estejam, criar um objeto padrão
  const stats = reportData.stats || {
    vehiclesInStock: 0,
    vehiclesSold: 0,
    totalExpenses: 0,
    totalProfit: 0
  };

  // Verificar se os dados de vendas estão disponíveis
  const sales = reportData.sales || {
    monthly: [],
    profit: {
      total: 0,
      byMonth: [],
      byVehicleType: []
    }
  };

  // Verificar se os dados de despesas estão disponíveis
  const expenses = reportData.expenses || {
    byCategory: [],
    byMonth: [],
    totalAmount: 0,
    totalCount: 0
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap' }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 500 }}>
          Relatórios
        </Typography>

        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel id="date-range-select-label">Período</InputLabel>
          <Select
            labelId="date-range-select-label"
            id="date-range-select"
            value={dateRange}
            label="Período"
            onChange={handleDateRangeChange}
          >
            <MenuItem value="3m">Últimos 3 meses</MenuItem>
            <MenuItem value="6m">Últimos 6 meses</MenuItem>
            <MenuItem value="1y">Último ano</MenuItem>
            <MenuItem value="all">Todo o período</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Cards de resumo */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 2 }}>
                <CarIcon />
              </Avatar>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Veículos em Estoque
                </Typography>
                <Typography variant="h6">
                  {stats.vehiclesInStock}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar sx={{ bgcolor: theme.palette.success.main, mr: 2 }}>
                <BarChartIcon />
              </Avatar>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Veículos Vendidos
                </Typography>
                <Typography variant="h6">
                  {stats.vehiclesSold}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar sx={{ bgcolor: theme.palette.error.main, mr: 2 }}>
                <MoneyIcon />
              </Avatar>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Total de Despesas
                </Typography>
                <Typography variant="h6">
                  {formatCurrency(stats.totalExpenses)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar sx={{ bgcolor: theme.palette.warning.main, mr: 2 }}>
                <TrendingUpIcon />
              </Avatar>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Lucro Total (caixa)
                </Typography>
                <Typography variant="h6">
                  {formatCurrency(stats.totalProfit)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Gráficos */}
      <Grid container spacing={3}>
        {/* Gráfico de vendas mensais */}
        <Grid item xs={12}>
          <MonthlyBarChart
            title="Vendas por Mês"
            data={sales.monthly || []}
            height={400}
            color={theme.palette.primary.main}
            valueLabel="Vendas (R$)"
          />
        </Grid>

        {/* Gráfico de lucro mensal */}
        <Grid item xs={12} md={6}>
          <LineChartComponent
            title="Lucro Mensal (caixa)"
            data={{
              labels: formatMonthlyData(dateRange, sales.profit.byMonth || []).map(item => 
                formatMonthDate(item.month)
              ),
              values: formatMonthlyData(dateRange, sales.profit.byMonth || []).map(item => item.profit),
              label: 'Lucro (R$)'
            }}
            height={380}
            color="#2ecc71"
            backgroundColor="rgba(46, 204, 113, 0.2)"
          />
        </Grid>

        {/* Gráfico de despesas por categoria */}
        <Grid item xs={12} md={6}>
          <PieChartComponent
            title="Despesas por Categoria"
            data={{
              labels: (expenses.byCategory || []).map(item => EXPENSE_CATEGORIES[item.category as ExpenseCategory] || 'Outro'),
              values: (expenses.byCategory || []).map(item => item.amount)
            }}
            height={380}
            colors={Object.values(EXPENSE_CATEGORY_COLORS)}
          />
        </Grid>

        {/* Margem de lucro por veículo */}
        <Grid item xs={12} md={6}>
          <BarChartComponent
            title="Margem de Lucro por Veículo"
            data={{
              labels: (sales.profit.byVehicleType || []).map(item => item.vehicleType || 'Outros'),
              values: (sales.profit.byVehicleType || []).map(item => item.averageProfit),
              label: 'Média de Lucro (R$)'
            }}
            height={380}
            color={theme.palette.info.main}
          />
        </Grid>

        {/* Despesas mensais */}
        <Grid item xs={12} md={6}>
          <MonthlyBarChart
            title="Despesas Mensais"
            data={expenses.byMonth || []}
            height={380}
            color={theme.palette.error.main}
            valueLabel="Total de Despesas (R$)"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default Reports; 